import { useEffect, useEffectEvent, useRef } from 'react';

import InputManager from '~/modules/game-engine/input/input-manager';
import PlayersManager from '~/modules/players/players-manager';

const CHECK_INTERVAL_MS = 500;

/**
 * Three seconds of every singer at once being unreachable.
 *
 * A single phone dropping never gets here - one singer losing their microphone is theirs to sort
 * out, and stopping the song for the rest of the room would cost them more than it gives back.
 * What this is for is the room where nothing on stage can be heard at all: the song plays on to
 * nobody and every note of it is recorded as a miss.
 *
 * Sustained rather than instant because a microphone reads as `unavailable` after 1500ms of
 * silence, which a phone riding out a Wi-Fi roam or a screen that just locked clears on its own
 * well inside three seconds.
 */
const CHECKS_BEFORE_PAUSING = 6;

const usePauseOnLostInputs = (enabled: boolean, onAllInputsLost: () => void) => {
  const pause = useEffectEvent(onAllInputsLost);
  const lostChecks = useRef(0);
  // Disarmed by firing, so resuming with the microphones still gone does not put the menu straight
  // back up. One of them coming back is what arms it again.
  const armed = useRef(true);

  useEffect(() => {
    if (!enabled) return;

    lostChecks.current = 0;
    const interval = setInterval(() => {
      const players = PlayersManager.getPlayers();
      const allLost =
        players.length > 0 && players.every((player) => InputManager.getInputStatus(player.number) === 'unavailable');

      if (!allLost) {
        lostChecks.current = 0;
        armed.current = true;

        return;
      }

      lostChecks.current += 1;
      if (armed.current && lostChecks.current >= CHECKS_BEFORE_PAUSING) {
        armed.current = false;
        pause();
      }
    }, CHECK_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [enabled]);
};

export default usePauseOnLostInputs;
